import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface WhoopDataPoint {
  date: string;
  recoveryScore: number | null;
  hrvRmssd: number | null;
  restingHeartRate: number | null;
  sleepPerformance: number | null;
  sleepDuration: number | null;
  deepSleep: number | null;
  remSleep: number | null;
  dayStrain: number | null;
  calories: number | null;
  avgHeartRate: number | null;
}

export function useWhoopCorrelationData(days: number = 30) {
  const [data, setData] = useState<WhoopDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasWhoopSubscription, setHasWhoopSubscription] = useState(false);

  useEffect(() => {
    loadWhoopData();
  }, [days]);

  const loadWhoopData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if user has WHOOP connected
      const { data: whoopConnection } = await supabase
        .from('whoop_connections')
        .select('is_active')
        .eq('user_id', user.id)
        .single();

      setHasWhoopSubscription(whoopConnection?.is_active || false);

      if (!whoopConnection?.is_active) {
        setIsLoading(false);
        return;
      }

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      // Fetch all WHOOP data in parallel
      const [recoveryData, sleepData, cycleData, workoutData] = await Promise.all([
        supabase
          .from('whoop_recovery_data')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startStr)
          .lte('recorded_at', endStr)
          .order('recorded_at', { ascending: true }),
        supabase
          .from('whoop_sleep_data')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startStr)
          .lte('recorded_at', endStr)
          .order('recorded_at', { ascending: true }),
        supabase
          .from('whoop_cycle_data')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startStr)
          .lte('recorded_at', endStr)
          .order('recorded_at', { ascending: true }),
        supabase
          .from('whoop_workout_data')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startStr)
          .lte('recorded_at', endStr)
          .order('recorded_at', { ascending: true })
      ]);

      // Combine data by date
      const dataByDate: Record<string, WhoopDataPoint> = {};

      // Process recovery data
      recoveryData.data?.forEach((item: any) => {
        const date = item.recorded_at.split('T')[0];
        if (!dataByDate[date]) {
          dataByDate[date] = {
            date,
            recoveryScore: null,
            hrvRmssd: null,
            restingHeartRate: null,
            sleepPerformance: null,
            sleepDuration: null,
            deepSleep: null,
            remSleep: null,
            dayStrain: null,
            calories: null,
            avgHeartRate: null
          };
        }
        dataByDate[date].recoveryScore = item.recovery_score;
        dataByDate[date].hrvRmssd = item.hrv_rmssd;
        dataByDate[date].restingHeartRate = item.resting_heart_rate;
      });

      // Process sleep data
      sleepData.data?.forEach((item: any) => {
        const date = item.recorded_at.split('T')[0];
        if (!dataByDate[date]) {
          dataByDate[date] = {
            date,
            recoveryScore: null,
            hrvRmssd: null,
            restingHeartRate: null,
            sleepPerformance: null,
            sleepDuration: null,
            deepSleep: null,
            remSleep: null,
            dayStrain: null,
            calories: null,
            avgHeartRate: null
          };
        }
        dataByDate[date].sleepPerformance = item.sleep_performance_percentage;
        dataByDate[date].sleepDuration = item.sleep_duration_minutes;
        dataByDate[date].deepSleep = item.deep_sleep_minutes;
        dataByDate[date].remSleep = item.rem_sleep_minutes;
      });

      // Process cycle data
      cycleData.data?.forEach((item: any) => {
        const date = item.recorded_at.split('T')[0];
        if (!dataByDate[date]) {
          dataByDate[date] = {
            date,
            recoveryScore: null,
            hrvRmssd: null,
            restingHeartRate: null,
            sleepPerformance: null,
            sleepDuration: null,
            deepSleep: null,
            remSleep: null,
            dayStrain: null,
            calories: null,
            avgHeartRate: null
          };
        }
        dataByDate[date].dayStrain = item.day_strain;
        dataByDate[date].calories = item.calories;
        dataByDate[date].avgHeartRate = item.avg_heart_rate;
      });

      setData(Object.values(dataByDate));
    } catch (error) {
      console.error('Error loading WHOOP correlation data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { data, isLoading, hasWhoopSubscription, refresh: loadWhoopData };
}
